import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { showToast } from '../../../Components/ToastNotifications';

const ClassificationsUpdate = () => {
  const navigate = useNavigate();
  const { divisionInstitutionsId } = useParams();

  const { register, setValue, watch, handleSubmit } = useForm({
    defaultValues: {
      classifications: [],
    },
  });

  const classifications = watch('classifications', []);
  const [isLoading, setIsloading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [types, setTypes] = useState([]);
  const [batchId, setBatchId] = useState(null);

  // Type options depend on the batch's formula version (v1 -> PAT/CB/ATT, v2 -> CB)
  useEffect(() => {
    if (!batchId) return;
    const getTypes = async () => {
      try {
        const data = await api.getClassificationFinalTypes({ domain: 'HS', batchId });
        setTypes(data || []);
      } catch (error) {
        console.error('Failed to get classification types:', error);
      }
    };
    getTypes();
  }, [batchId]);

  useEffect(() => {
    const getInstitutions = async () => {
      try {
        const data = await api.getClassificationsHardskillByDivisiLembagaId(
          {
            id: divisionInstitutionsId,
          },
        );

        if (data?.hardskill) {
          data.hardskill.forEach((item, index) => {
            setValue(
              `classifications.${index}.classificationId`,
              item.classificationId,
            );
            setValue(
              `classifications.${index}.classification`,
              item.classification,
            );
            setValue(
              `classifications.${index}.classificationTypeId`,
              item.classificationTypeId ?? '',
            );
          });
          setValue(`institutionsName`, data?.institutions?.institutionsName);
          setValue(`divisionName`, data?.institutions?.divisionName);
          setValue(`batch`, data?.batch?.batch);
          setBatchId(data?.batch?.batchId ?? null);
          setIsDataLoaded(true);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    getInstitutions();
  }, [divisionInstitutionsId, setValue]);

  const onSubmit = async (data) => {
    if (isLoading) return;
    setIsloading(true);
    try {
      const payload = {
        classifications: (data.classifications || []).map((item) => ({
          ...item,
          classificationTypeId: item.classificationTypeId
            ? parseInt(item.classificationTypeId, 10)
            : null,
        })),
      };
      const { status, message } = await api.updateClassifications({
        divisionInstitutionsId: divisionInstitutionsId,
        payload,
      });
      if (status !== 'success') {
        showToast(message, 'danger');
      } else {
        showToast(message, 'success');
        navigate('/mentor/classifications');
      }
    } catch (error) {
      console.error('Submission failed:', error);
      showToast(error.message || 'Gagal menyimpan perubahan. Silakan coba lagi.', 'danger');
    } finally {
      setIsloading(false);
    }
  };

  if (!isDataLoaded) {
    return (
      <div className='d-flex justify-content-center align-items-center py-5'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      {/* Header with Back Button */}
      <div className='d-flex align-items-center gap-3 mb-4'>
        <button
          onClick={() => navigate(-1)}
          className='btn btn-light rounded-circle shadow-none border d-flex align-items-center justify-content-center hover-translate-y transition-all'
          style={{ width: '40px', height: '40px' }}
        >
          <i className='bi bi-arrow-left'></i>
        </button>
        <div>
          <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold small mb-1'>
            KPI Management
          </span>
          <h1 className='h4 fw-bold mb-0 text-dark'>Ubah KPI Hardskill</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Info Card - Compact */}
        <div className='card border border-light-subtle bg-light mb-4 shadow-none'>
          <div className='card-body p-3'>
            <div className='row align-items-center g-3'>
              <div className='col-md-5'>
                <div className='d-flex flex-column'>
                  <span className='text-muted small fw-medium'>Lembaga</span>
                  <span className='fw-bold text-dark'>
                    {watch('institutionsName')}
                  </span>
                </div>
              </div>
              <div className='col-md-5'>
                <div className='d-flex flex-column'>
                  <span className='text-muted small fw-medium'>Divisi</span>
                  <span className='fw-bold text-dark'>
                    {watch('divisionName')}
                  </span>
                </div>
              </div>
              <div className='col-md-2 text-md-end text-start border-start border-light-subtle ps-md-4'>
                <div className='d-flex flex-column'>
                  <span className='text-muted small fw-medium text-nowrap'>
                    Batch Program
                  </span>
                  <span className='badge bg-white text-dark border px-3 py-1 align-self-md-end mt-1'>
                    {watch('batch')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className='row g-4'>
          {classifications?.map((value, key) => (
            <div
              key={key}
              className='col-12'
            >
              <div className='card border border-light-subtle border-start border-2 border-primary bg-white p-4 rounded-4 shadow-none transition-all'>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                    <span
                      className='bg-primary text-white rounded-circle d-flex align-items-center justify-content-center'
                      style={{
                        width: '24px',
                        height: '24px',
                        fontSize: '12px',
                      }}
                    >
                      {key + 1}
                    </span>
                    Indikator Kompetensi
                  </h6>
                  <select
                    {...register(`classifications.${key}.classificationTypeId`)}
                    className='form-select form-select-sm w-auto'
                  >
                    <option value=''>Manual</option>
                    {types.map((type) => (
                      <option
                        key={type.id}
                        value={type.id}
                      >
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  {...register(`classifications.${key}.classification`)}
                  rows={2}
                  className='form-control bg-light border-0 shadow-none'
                  placeholder='Deskripsikan KPI di sini...'
                  style={{ resize: 'none' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <div className='mt-5 d-flex justify-content-end'>
          <button
            type='submit'
            disabled={isLoading}
            className='btn btn-primary px-5 py-2 fw-bold rounded-pill transition-all d-flex align-items-center gap-2'
          >
            {isLoading ? (
              <>
                <span
                  className='spinner-border spinner-border-sm'
                  aria-hidden='true'
                ></span>
                Menyimpan...
              </>
            ) : (
              <>
                <i className='bi bi-check2-circle'></i>
                Simpan Perubahan
              </>
            )}
          </button>
        </div>
      </form>

      <style>{`
        .hover-translate-y:hover { transform: translateY(-3px); }
        .transition-all { transition: all 0.2s ease; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .card { border-radius: 1rem; }
      `}</style>
    </div>
  );
};

export default ClassificationsUpdate;
